/**
 * @file order-chain-texts.ts
 * @description Реестр текстов полосы «Сейчас» и главной кнопки по стадиям
 * (phase19.24-order-card-chain-design.md §13, таблица «Правила текста»).
 *
 * Менеджер читает три вопроса сверху вниз: где заказ, чей ход, что делать мне.
 * Ответ на третий — ровно одна фраза и ровно одна кнопка, поэтому формулировки
 * лежат реестром: новая стадия = запись, а не ветка в компоненте полосы.
 *
 * Тексты — функции от контекста (сумма, срок, прогресс сборки), но это всё
 * равно реестр: выбор записи идёт по ключу стадии, а не каскадом `if`.
 */

import type { DeliveryAgreementDto } from "./api-delivery-agreement";
import {
  ORDER_DELIVERY_CARRIER_LABEL,
  currencySymbol,
  formatMoney,
  type OrderDto,
  type OrderStageKind,
} from "./api-orders";
import type { OrderActionKey } from "./order-actions";
import {
  isAgreementRoute,
  isDeliveryUnknownRoute,
  isShopCourierRoute,
  type OrderChainDto,
  type OrderChainNodeKey,
  type OrderChainProblem,
  type OrderRouteDto,
  type OrderRouteStageDto,
} from "./order-chain";
import { receiptSummaryText } from "./receipt-method";
import {
  ORDER_ITEM_UNLINKED_TEXT,
  unlinkedCountPhrase,
  unlinkedOrderItems,
} from "./order-item-link";
import { formatDocDateTime } from "../components/wms/wms-document-meta";
import type { OrderTabKey } from "../hooks/useOrderTabParam";

/** Куда ведёт главная кнопка. Реестр целей — не `if` по стадиям. */
export type NowPrimaryTarget =
  /** Страница магазина по относительному пути: `wms/warehouses`. */
  | { kind: "shop"; path: string }
  /** Открыть документ цепочки (задание, отгрузку) — адрес берём из узла. */
  | { kind: "node"; node: OrderChainNodeKey }
  /** `focusId` — строка вкладки, которую надо раскрыть и обвести (§4). */
  | { kind: "tab"; tab: OrderTabKey; focusId?: string | null }
  | { kind: "anchor"; anchor: string }
  /** Переход заказа или действие рядом с ним: кнопка зовёт карточку. */
  | { kind: "action"; action: OrderActionKey };

export interface NowPrimary {
  label: string;
  target: NowPrimaryTarget;
}

/** Две строки полосы «Сейчас»: что происходит и что делать. */
export interface NowText {
  first: string;
  second: string | null;
}

export interface NowTextContext {
  order: OrderDto;
  stage: OrderRouteStageDto;
  chain: OrderChainDto | null;
  /** Маршрут самовывоза: тексты «Собран» и «Готов к выдаче» разные. */
  pickup: boolean;
  /** Ближайшая стадия, где ход снова за менеджером. */
  nextManager: OrderRouteStageDto | null;
  /**
   * Заявка «доставка по договорённости» (phase19.24 шаг 5.1): по ней полоса
   * различает шесть ситуаций согласования — от «исход не выбран» до
   * «клиент молчит третьи сутки». Поле опционально: без заявки полоса
   * говорит общими словами стадии, а не падает.
   */
  agreement?: DeliveryAgreementDto | null;
}

interface NowEntry {
  text: (ctx: NowTextContext) => NowText;
  primary?: (ctx: NowTextContext) => NowPrimary | null;
}

/** «20 мин», «2 ч 15 мин», «2 дня» — сколько заказ стоит в стадии. */
export function formatSince(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} ч ${minutes % 60} мин` : `${hours} ч`;
  const days = Math.floor(hours / 24);
  return `${days} дн`;
}

/** Сумма заказа прописью цифрами: «4 820 ₽». */
function total(order: OrderDto): string {
  return `${formatMoney(order.totalGross)} ${currencySymbol(order.currency)}`;
}

/** Перевозчик словами; неизвестен — «перевозчик». */
function carrier(order: OrderDto): string {
  const code = order.delivery?.carrier;
  return code ? ORDER_DELIVERY_CARRIER_LABEL[code] : "перевозчик";
}

/** Заказ везёт курьер магазина: маршрут первичен, сводка — запасной признак. */
function isShopCourierStage(ctx: NowTextContext): boolean {
  return isShopCourierRoute(ctx.chain?.route ?? null, ctx.order.delivery?.kind ?? null);
}

/**
 * «Адрес: Челябинск, ул. Труда, 1» — куда курьеру ехать. Адрес живёт в
 * снапшоте способа получения, который карточка читает вместе с заявкой;
 * его нет — фразу не выдумываем.
 */
function deliveryAddressLine(ctx: NowTextContext): string | null {
  const address = ctx.agreement?.receipt?.address?.trim();
  return address ? `Адрес: ${address}` : null;
}

/** Прогресс сборки из узла задания: «5 из 7 позиций». */
function pickingProgress(chain: OrderChainDto | null): string | null {
  const node = chain?.nodes.find((n) => n.key === "PICKING");
  const progress = node?.detail?.progress;
  if (!progress) return null;
  return `${progress.done} из ${progress.total} позиций`;
}

/** «Ваш следующий шаг — «Передать в ТК»»; нет такой стадии — null. */
function nextStepPhrase(ctx: NowTextContext): string | null {
  if (!ctx.nextManager) return null;
  return `Ваш следующий шаг — «${ctx.nextManager.name}»`;
}

/** Срок стадии человеческим текстом: «до 15.09 10:12». */
function untilDeadline(stage: OrderRouteStageDto): string {
  return stage.deadlineAt ? ` до ${formatDocDateTime(stage.deadlineAt)}` : "";
}

/**
 * Деньги за заказ получены полностью?
 *
 * Только серверная сводка `order.payment` (phase19.24): legacy-поле
 * `paymentStatus` живёт своей жизнью у Avito-заказов и после частичных
 * возвратов, и кнопка «Выдать заказ» по нему открывалась бы на неоплаченном
 * самовывозе. Сводки нет (старый ответ сервера) — считаем, что не оплачено:
 * лишний шаг «Отметить оплату» безопаснее выдачи без денег.
 */
function isFullyPaid(order: OrderDto): boolean {
  return order.payment?.status === "FULL";
}

/** Что нужно знать про заказ, чтобы решить, согласуем ли мы доставку. */
export interface DeliveryAgreementNeedInput {
  order: OrderDto;
  route: OrderRouteDto | null;
  agreement?: DeliveryAgreementDto | null;
}

/**
 * Нужно ли сначала согласовать доставку (решение пользователя 2026-09-21,
 * уточнение ревью 5.1).
 *
 * Да, если способ получения не выбран, если маршрут и так идёт через
 * согласование (F30) или если заказ завёл менеджер вручную (не с витрины) и
 * клиент ещё не подтвердил условия письменно. Стоимость доставки меняет сумму
 * заказа, поэтому согласование идёт ДО оплаты, а не после подтверждения.
 *
 * Проверяем именно `agreement.confirmedAt`, а не наличие `delivery.kind`:
 * черновик, где менеджер уже выбрал самовывоз, для сервера всё равно не
 * подтверждён, и «Подтвердить» возвращало 409 («кнопка не работает»).
 */
export function needsDeliveryAgreement(input: DeliveryAgreementNeedInput): boolean {
  const { order, route } = input;
  return (
    isDeliveryUnknownRoute(route) ||
    isAgreementRoute(route) ||
    (order.source !== "SITE" && !input.agreement?.confirmedAt)
  );
}

/** То же правило для контекста полосы «Сейчас». */
function needsAgreementInContext(ctx: NowTextContext): boolean {
  return needsDeliveryAgreement({
    order: ctx.order,
    route: ctx.chain?.route ?? null,
    agreement: ctx.agreement ?? null,
  });
}

/** Открыть блок заявки в карточке — цель по умолчанию для согласования. */
const OPEN_AGREEMENT: NowPrimary = {
  label: "Согласовать доставку",
  // Вкладка, а не якорь: с шага 5.2 условия доставки живут во вкладке
  // «Доставка» — единственном их доме (проект шага 5.2 §2).
  target: { kind: "tab", tab: "delivery" },
};

/**
 * Шесть ситуаций согласования доставки (проект шага 5.1 §5): от «исход не
 * выбран» до «клиент молчит третьи сутки». Ситуацию определяют ДАННЫЕ заявки,
 * а не отдельный флаг стадии — иначе полоса и блок заявки говорили бы разное.
 *
 * Порядок проверок = порядок срочности: незаполненный пункт самовывоза важнее
 * просрочки (письмо с таким адресом отправлять нельзя вовсе), устаревшая
 * заявка важнее «ждём клиента» (ждать уже нечего).
 */
function agreementSituation(ctx: NowTextContext): { text: NowText; primary: NowPrimary | null } {
  const a = ctx.agreement ?? null;
  const receipt = a?.receipt ?? null;

  if (receipt?.method === "PICKUP" && !receipt.pickup?.address) {
    return {
      text: {
        first: "Не заполнен адрес пункта самовывоза",
        second: "Заполните адрес склада в настройках — иначе клиенту некуда ехать",
      },
      primary: {
        label: "В настройки складов",
        target: { kind: "shop", path: "wms/warehouses" },
      },
    };
  }
  if (a?.status === "OUTDATED") {
    return {
      text: {
        first: "Заявка недействительна",
        second: "Заказ изменили — выставите условия заново и отправьте клиенту",
      },
      primary: OPEN_AGREEMENT,
    };
  }
  if (a?.status === "AWAITING_CONFIRMATION") {
    const expiresAt = a.expiresAt;
    const overdue = !!expiresAt && new Date(expiresAt).getTime() < Date.now();
    if (overdue) {
      return {
        text: {
          first: "Клиент не подтвердил за 72 часа",
          second: "Позвоните или отправьте заново — иначе отмена по таймеру",
        },
        primary: {
          label: "Отправить повторно",
          target: { kind: "action", action: "sendAgreement" },
        },
      };
    }
    return {
      text: {
        first: `Ждём подтверждения${expiresAt ? ` до ${formatDocDateTime(expiresAt)}` : ""}`,
        second: "Клиент подтверждает по ссылке · дальше — выставить оплату",
      },
      primary: OPEN_AGREEMENT,
    };
  }
  if (receipt) {
    return {
      text: {
        first: `${receiptSummaryText(receipt.method, {
          label: receipt.label,
          carrierName: receipt.carrierName,
          amount: receipt.deliveryAmount === null ? null : Number(receipt.deliveryAmount),
        })} — письмо не отправлено`,
        second: "Отправьте предложение: клиент подтвердит его по ссылке",
      },
      primary: {
        label: "Отправить на подтверждение",
        target: { kind: "action", action: "sendAgreement" },
      },
    };
  }
  return {
    text: {
      first: "Согласуйте способ получения и сумму",
      second: "Клиент не увидит заказ, пока вы не отправите письмо",
    },
    primary: OPEN_AGREEMENT,
  };
}

/**
 * Реестр по стадиям F31. Ключ — системный `kind`, текст магазина берётся из
 * `stage.name`: магазин может переименовать стадию, смысл записи не меняется.
 */
export const ORDER_NOW_TEXTS: Record<OrderStageKind, NowEntry> = {
  CREATED: {
    text: (ctx) =>
      needsAgreementInContext(ctx)
        ? {
            first: "Согласуйте доставку с клиентом",
            second:
              "Способ и стоимость доставки влияют на сумму заказа — согласуйте до оплаты",
          }
        : {
            first: "Подтвердите заказ",
            second: "После подтверждения клиент получит счёт или ссылку на оплату",
          },
    primary: (ctx) =>
      needsAgreementInContext(ctx)
        ? {
            label: "Согласовать доставку",
            target: { kind: "action", action: "agreeDelivery" },
          }
        : { label: "Подтвердить", target: { kind: "action", action: "confirm" } },
  },
  PENDING_DELIVERY_AGREEMENT: {
    text: (ctx) => agreementSituation(ctx).text,
    primary: (ctx) => agreementSituation(ctx).primary,
  },
  AWAITING_PAYMENT: {
    text: (ctx) => ({
      first: `Ждём оплату ${total(ctx.order)}${untilDeadline(ctx.stage)}`,
      // Доставку ещё не выбрали — сумма не окончательная, и предупредить об
      // этом важнее подсказки про наличные (решение 2026-09-21).
      second: isDeliveryUnknownRoute(ctx.chain?.route ?? null)
        ? "Доставка не выбрана — согласуйте её до оплаты"
        : "Если клиент платит наличными или переводом — отметьте оплату вручную",
    }),
    primary: () => ({ label: "Отметить оплату", target: { kind: "tab", tab: "payments" } }),
  },
  PAID: {
    text: () => ({
      first: "Передайте заказ на сборку",
      second: "Склад получит задание, вы увидите прогресс здесь",
    }),
    primary: () => ({
      label: "Начать сборку",
      target: { kind: "action", action: "startAssembling" },
    }),
  },
  PACKAGING: {
    text: (ctx) => {
      const progress = pickingProgress(ctx.chain);
      return {
        first: progress ? `Ждём склад · ${progress}` : "Ждём склад",
        second: nextStepPhrase(ctx)
          ? `${nextStepPhrase(ctx)} после проведения сборки`
          : "Стадию ставит проведение задания — кнопки здесь нет",
      };
    },
    primary: () => ({ label: "Открыть задание", target: { kind: "node", node: "PICKING" } }),
  },
  // Три разных «собран»: самовывоз ждёт клиента, ТК ждёт отгрузки, а свой
  // курьер везёт сам — там ни перевозчика, ни вкладки отгрузок нет, и заказ
  // закрывает менеджер кнопкой «Вручить заказ» (находка ревью 5.1).
  PACKED: {
    text: (ctx) => {
      if (ctx.pickup) {
        return {
          first: "Сообщите клиенту, что заказ готов",
          second: "При выдаче отметьте оплату, если она при получении",
        };
      }
      if (isShopCourierStage(ctx)) {
        return {
          first: "Заказ собран — вручите клиенту силами магазина",
          second: deliveryAddressLine(ctx) ?? "Адрес доставки — в блоке согласования",
        };
      }
      return {
        first: `Передайте заказ в доставку (${carrier(ctx.order)})`,
        second: "Создастся отгрузка и трек, дальше заказ ведёт перевозчик",
      };
    },
    primary: (ctx) => {
      if (ctx.pickup) return null;
      if (isShopCourierStage(ctx)) {
        return { label: "Вручить заказ", target: { kind: "action", action: "handOver" } };
      }
      return { label: "Передать в ТК", target: { kind: "tab", tab: "shipments" } };
    },
  },
  HANDED_TO_CARRIER: {
    text: (ctx) => ({
      first: `Заказ ведёт ${carrier(ctx.order)}`,
      second: "Вмешательство нужно, если статус не менялся больше 3 дней",
    }),
    primary: () => ({ label: "Открыть отгрузку", target: { kind: "tab", tab: "shipments" } }),
  },
  // Оплата решает, что менеджер делает у стойки: отдать заказ или сначала
  // принять деньги. Две разные кнопки — иначе «Выдать» на неоплаченном
  // заказе закрывает стадию раньше кассы.
  READY_FOR_PICKUP: {
    text: (ctx) =>
      isFullyPaid(ctx.order)
        ? {
            first: `Ждём клиента${untilDeadline(ctx.stage)} · оплачено`,
            second: "При визите выдайте заказ",
          }
        : {
            first: `Ждём клиента${untilDeadline(ctx.stage)}`,
            second: "При визите примите оплату и выдайте заказ",
          },
    primary: (ctx) =>
      isFullyPaid(ctx.order)
        ? { label: "Выдать заказ", target: { kind: "action", action: "handOver" } }
        : { label: "Отметить оплату", target: { kind: "tab", tab: "payments" } },
  },
  IN_TRANSIT: {
    text: (ctx) => ({
      first: `В пути · ведёт ${carrier(ctx.order)}`,
      second: "Вмешательство нужно, если статус не менялся больше 3 дней",
    }),
    primary: () => ({ label: "Открыть отгрузку", target: { kind: "tab", tab: "shipments" } }),
  },
  ARRIVED_AT_PICKUP: {
    text: (ctx) => ({
      first: `Заказ в пункте выдачи${untilDeadline(ctx.stage)}`,
      second: `Клиент забирает сам, ${carrier(ctx.order)} уведомит о вручении`,
    }),
    primary: () => ({ label: "Открыть отгрузку", target: { kind: "tab", tab: "shipments" } }),
  },
  // Постоплата: заказ вручён, а деньги могли не дойти — завершать такой
  // заказ нельзя, сначала платёж.
  DELIVERED: {
    text: (ctx) =>
      isFullyPaid(ctx.order)
        ? {
            first: "Заказ доставлен",
            second: "Завершите заказ — это закроет сделку и запустит запрос отзыва",
          }
        : {
            first: "Заказ доставлен · оплата не поступила",
            second: "Отметьте оплату, после неё заказ можно завершить",
          },
    primary: (ctx) =>
      isFullyPaid(ctx.order)
        ? { label: "Завершить", target: { kind: "action", action: "complete" } }
        : { label: "Отметить оплату", target: { kind: "tab", tab: "payments" } },
  },
  COMPLETED: {
    text: () => ({ first: "Заказ завершён", second: "Можно запросить отзыв о покупке" }),
    primary: () => ({
      label: "Запросить отзыв",
      target: { kind: "action", action: "requestReview" },
    }),
  },
  CANCELLED: {
    text: (ctx) => ({
      first: `Заказ отменён${ctx.order.cancelledAt ? ` ${formatDocDateTime(ctx.order.cancelledAt)}` : ""}`,
      second: ctx.order.cancelReason ?? "Проверьте возврат средств клиенту",
    }),
  },
  RETURN: {
    text: () => ({
      first: "Возврат в работе",
      second: "Проверьте состав возврата и возврат средств",
    }),
    primary: () => ({ label: "Открыть возврат", target: { kind: "tab", tab: "returns" } }),
  },
};

/** Проблема перебивает текст стадии: красная полоса и своя кнопка (§14). */
export const ORDER_NOW_PROBLEM_TEXTS: Record<OrderChainProblem, NowEntry> = {
  SHORTAGE: {
    text: (ctx) => ({
      first: `Недостача при сборке${pickingProgress(ctx.chain) ? ` · ${pickingProgress(ctx.chain)}` : ""}`,
      second: "Замена, частичная отгрузка или отмена. Заказ стоит, пока вы не решите",
    }),
    primary: () => ({ label: "Решить недостачу", target: { kind: "node", node: "PICKING" } }),
  },
  AGREEMENT_OUTDATED: {
    text: () => ({
      first: "Заявка на доставку недействительна",
      second: "Заказ изменили после отправки — выставите стоимость заново",
    }),
    primary: () => ({ label: "Открыть заявку", target: { kind: "anchor", anchor: "agreement" } }),
  },
  CARRIER_SILENT: {
    text: (ctx) => ({
      first: `${carrier(ctx.order)} не обновляет статус`,
      second: "Проверьте трек или позвоните в службу доставки",
    }),
    primary: () => ({ label: "Открыть отгрузку", target: { kind: "tab", tab: "shipments" } }),
  },
  // OQ-54: позицию без товара каталога собрать нельзя, и заказ стоит. Кнопка
  // ведёт сразу к проблемной строке — искать её в составе менеджер не должен.
  ITEM_UNLINKED: {
    text: (ctx) => {
      const count = unlinkedOrderItems(ctx.order.items).length;
      return {
        first: count
          ? `${unlinkedCountPhrase(count)} к товару — сборка стоит`
          : "Позиция не привязана к товару — сборка стоит",
        second: "Привяжите товар во вкладке «Позиции»",
      };
    },
    primary: (ctx) => ({
      label: ORDER_ITEM_UNLINKED_TEXT.action,
      target: {
        kind: "tab",
        tab: "items",
        focusId: unlinkedOrderItems(ctx.order.items)[0]?.id ?? null,
      },
    }),
  },
};
